import { useMutation } from "@tanstack/react-query";
import { useRouter, useSearchParams } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { useState } from "react";
import { EVENTS, useAnalytics } from "@/hooks/useAnalytics";
import { resolveTrimbleDestination } from "@/lib/auth/resolveDestination.client";
import { resolvePostLoginDestination } from "@/lib/auth/resolvePostLoginDestination.client";
import type { TokenResponse } from "@/lib/authBackend/trimble/tid/client";
import { createDxTrialLead, createDxTrialSignup, DxTrialSignupError } from "@/lib/dxTrials/client";
import { buildTrialErrorPath, clearTrialSignupFailure, markTrialSignupFailed } from "@/lib/dxTrials/trialSignupFailure";
import { isTrialLeadFormValues, type RegisterTrialPublicController, type RegisterTrialPublicFormValues, type TrialLeadFormValues, type TrialProvisioningFormValues } from "../../register-public.types";
import { resolveTrialAttribution, toTrialAttributionPayload } from "../../trialPrefill";
import { usePublicTrialTurnstile } from "../usePublicTrialTurnstile/usePublicTrialTurnstile";
type TidTrialSignup = {
  getTokens: () => Promise<TokenResponse>;
};
type TidTrialLead = {
  // Runs only once the lead is safely captured, so an abandoned Trimble ID sign-in
  // still leaves a marketing record behind.
  onLeadCaptured: (values: TrialLeadFormValues) => Promise<void>;
};
export function useRegisterTrialPublic(options: TidTrialSignup | TidTrialLead): RegisterTrialPublicController {
  const tidTrialSignup = "getTokens" in options ? options : undefined;
  const tidTrialLead = "onLeadCaptured" in options ? options : undefined;
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations("trials.register_public.errors");
  const searchParams = useSearchParams();
  const attribution = toTrialAttributionPayload(resolveTrialAttribution(searchParams));
  const {
    trackEvent
  } = useAnalytics();
  const turnstile = usePublicTrialTurnstile();
  const [trialSignupOutcome, setTrialSignupOutcome] = useState<RegisterTrialPublicController["trialSignupOutcome"]>(null);
  const submitMutation = useMutation({
    mutationFn: async (values: RegisterTrialPublicFormValues | TrialProvisioningFormValues) => {
      if (tidTrialLead) {
        if (!turnstile.token || !isTrialLeadFormValues(values)) return;
        await captureTrialLead(values, turnstile.token);
        await tidTrialLead.onLeadCaptured(values);
        return;
      }
      if (tidTrialSignup) {
        setTrialSignupOutcome(null);
        clearTrialSignupFailure();
        if (isTrialLeadFormValues(values)) {
          if (!turnstile.token) return;
          await captureTrialLead(values, turnstile.token);
        }
        try {
          const tokens = await tidTrialSignup.getTokens();
          const signup = await createDxTrialSignup({
            companyName: values.company,
            companyRole: values.companyRole,
            phone: "phone" in values ? values.phone : undefined,
            acceptedTerms: true,
            idToken: tokens.id_token,
            accessToken: tokens.access_token,
            attribution
          });
          const postLoginTarget = await resolvePostLoginDestination(`Bearer ${tokens.access_token}`);
          const destination = await resolveTrimbleDestination(tokens.access_token, postLoginTarget, signup.organizationId);
          if (destination.kind === "account_disabled") {
            setTrialSignupOutcome({
              kind: "failed",
              message: t("account_disabled")
            });
            return;
          }
          if (destination.kind === "auth_unavailable") {
            setTrialSignupOutcome({
              kind: "failed",
              message: t("auth_unavailable")
            });
            return;
          }
          if (destination.kind === "no_access") {
            setTrialSignupOutcome({
              kind: "failed",
              message: t("no_access")
            });
            return;
          }
          trackEvent(EVENTS.PUBLIC_TRIAL_REGISTERED, {
            registration_method: "trimble"
          });
          router.replace(`/${locale}${destination.path}`);
        } catch (error) {
          if (error instanceof DxTrialSignupError && error.status === 422) {
            setTrialSignupOutcome({
              kind: "ineligible",
              status: eligibilityStatus(error.body)
            });
            return;
          }
          // Pushed rather than replaced: the entries behind this one are Trimble's own
          // sign-up pages, so without a history entry of its own Back re-enters a handoff
          // the visitor already completed.
          const supportNotified = error instanceof DxTrialSignupError && error.supportNotified;
          markTrialSignupFailed(supportNotified);
          router.push(`/${locale}${buildTrialErrorPath(supportNotified)}`);
        }
        return;
      }
      throw new Error("Missing Trimble ID registration flow");
    }
  });
  return {
    isSubmitting: submitMutation.isPending,
    submit: submitMutation.mutateAsync,
    turnstile,
    trialSignupOutcome
  };
}
function captureTrialLead(values: TrialLeadFormValues, turnstileToken: string): Promise<void> {
  return createDxTrialLead({
    firstName: values.firstName,
    lastName: values.lastName,
    email: values.email,
    phone: values.phone,
    company: values.company,
    country: values.country,
    state: values.state,
    marketingOptIn: values.marketingOptIn,
    acceptedTerms: true,
    turnstileToken
  });
}
function eligibilityStatus(body: unknown): string {
  const eligibilityError = dxTrialEligibilityError(body);
  const status = eligibilityError.status ?? eligibilityError.trialStatus;
  return typeof status === "string" ? status : "INELIGIBLE";
}
function dxTrialEligibilityError(body: unknown): Record<string, unknown> {
  if (!body || typeof body !== "object") return {};
  const record = body as Record<string, unknown>;
  const error = Array.isArray(record.errors) && record.errors[0] && typeof record.errors[0] === "object" && !Array.isArray(record.errors[0]) ? record.errors[0] as Record<string, unknown> : record;
  return error.meta && typeof error.meta === "object" && !Array.isArray(error.meta) ? error.meta as Record<string, unknown> : error;
}